import type { ApiClient } from '../api/client.js';
import {
  authenticatedSchema,
  loginResultSchema,
  meSchema,
  otpChallengeSchema,
  passwordResetDoneSchema,
  pinSetSchema,
  sessionListSchema,
  signedOutSchema,
  verificationSchema,
  type Authenticated,
  type LoginResult,
  type Me,
  type OtpChallenge,
  type SessionSummary,
  type Verification,
} from './auth-contract.js';

/**
 * Every call to `/api/v1/auth/*`.
 *
 * Note `skipAuthRefresh` on the credential endpoints. The API client transparently refreshes
 * and replays once on any 401 — which is exactly right for `/customers` and exactly wrong for
 * `/login`: a mistyped password would trigger a refresh attempt, and on a machine with a stale
 * cookie that refresh could succeed, replaying the failed login and confusing the answer. A
 * credential call gets one attempt and one answer.
 */

export async function startSignup(client: ApiClient, email: string): Promise<OtpChallenge> {
  return await client.post(
    '/auth/signup/start',
    { email },
    { schema: otpChallengeSchema, skipAuthRefresh: true },
  );
}

export async function verifySignup(
  client: ApiClient,
  input: { email: string; code: string },
): Promise<Verification> {
  return await client.post('/auth/signup/verify', input, {
    schema: verificationSchema,
    skipAuthRefresh: true,
  });
}

export async function completeSignup(
  client: ApiClient,
  input: { verificationToken: string; ownerName: string; shopName: string; password: string },
): Promise<Authenticated> {
  return await client.post('/auth/signup/complete', input, {
    schema: authenticatedSchema,
    skipAuthRefresh: true,
  });
}

export async function loginWithPassword(
  client: ApiClient,
  input: { email: string; password: string },
): Promise<LoginResult> {
  return await client.post('/auth/login', input, {
    schema: loginResultSchema,
    skipAuthRefresh: true,
  });
}

export async function loginWithPin(
  client: ApiClient,
  input: { email: string; pin: string },
): Promise<LoginResult> {
  return await client.post('/auth/login/pin', input, {
    schema: loginResultSchema,
    skipAuthRefresh: true,
  });
}

export async function loginWithGoogle(client: ApiClient, credential: string): Promise<LoginResult> {
  return await client.post(
    '/auth/login/google',
    { credential },
    { schema: loginResultSchema, skipAuthRefresh: true },
  );
}

export async function selectShop(
  client: ApiClient,
  input: { selectionToken: string; tenantId: string },
): Promise<Authenticated> {
  return await client.post('/auth/select-shop', input, {
    schema: authenticatedSchema,
    skipAuthRefresh: true,
  });
}

/**
 * Who is signed in. The one call the app makes on boot — and the only one that MAY refresh,
 * because a page opened after lunch with an expired access cookie and a live refresh cookie is
 * a working session that must not be sent to the login screen.
 */
export async function fetchMe(client: ApiClient): Promise<Me> {
  return await client.get('/auth/me', { schema: meSchema });
}

export async function signOut(client: ApiClient): Promise<void> {
  await client.post('/auth/logout', undefined, {
    schema: signedOutSchema,
    skipAuthRefresh: true,
  });
}

export async function signOutEverywhere(client: ApiClient): Promise<void> {
  await client.post('/auth/logout-all', undefined, { schema: signedOutSchema });
}

export async function listSessions(client: ApiClient): Promise<SessionSummary[]> {
  const { items } = await client.get('/auth/sessions', { schema: sessionListSchema });
  return items;
}

export async function revokeSession(client: ApiClient, sessionId: string): Promise<void> {
  await client.del(`/auth/sessions/${sessionId}`, undefined, { schema: signedOutSchema });
}

// --- Password reset by OTP -----------------------------------------------------------------

export async function startPasswordReset(client: ApiClient, email: string): Promise<OtpChallenge> {
  return await client.post(
    '/auth/password-reset/start',
    { email },
    { schema: otpChallengeSchema, skipAuthRefresh: true },
  );
}

export async function verifyPasswordReset(
  client: ApiClient,
  input: { email: string; code: string },
): Promise<Verification> {
  return await client.post('/auth/password-reset/verify', input, {
    schema: verificationSchema,
    skipAuthRefresh: true,
  });
}

export async function completePasswordReset(
  client: ApiClient,
  input: { verificationToken: string; password: string },
): Promise<{ revokedSessions: number }> {
  const result = await client.post('/auth/password-reset/complete', input, {
    schema: passwordResetDoneSchema,
    skipAuthRefresh: true,
  });
  return { revokedSessions: result.revokedSessions };
}

/** An owner or manager setting a tailor's PIN. The tailor never types their own. */
export async function setStaffPin(
  client: ApiClient,
  input: { userId: string; pin: string },
): Promise<void> {
  await client.post('/auth/staff-pin', input, { schema: pinSetSchema });
}
